import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  BarChart3,
  Clock,
  FileUp,
  Info,
  Loader2,
  Play,
  ShieldAlert,
  Target,
} from 'lucide-react'
import type { TestSetListItem } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'
import { testSetsApi, testSetsQueryKey } from '../api'
import { UploadTestSetDrawer } from '../components/UploadTestSetDrawer'
import { CRITICAL_LEVELS, diagnoseBase, formatBaseName } from '../lib/base-diagnosis'

function pct(v: number | null): string {
  if (v === null || Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function precisionColor(v: number | null): string {
  if (v === null) return 'text-fg-3'
  if (v >= 0.8) return 'text-success'
  if (v >= 0.5) return 'text-warning'
  return 'text-danger'
}

function MiniStat({
  icon: Icon,
  label,
  value,
  tone = 'neutral',
}: {
  icon: typeof Target
  label: string
  value: string
  tone?: 'neutral' | 'danger' | 'success'
}) {
  const valueColor = tone === 'danger' ? 'text-danger' : tone === 'success' ? 'text-success' : 'text-fg-1'
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
      <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider text-fg-3 mb-1.5">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={cn('font-display text-xl font-bold data-mono', valueColor)}>{value}</div>
    </div>
  )
}

function Metric({ label, value, valueClass, tip }: { label: string; value: string; valueClass?: string; tip: string }) {
  return (
    <div className="text-right">
      <div className="flex items-center justify-end gap-1 text-2xs uppercase tracking-wider text-fg-3">
        {label}
        <Tooltip content={tip} align="end">
          <Info className="h-2.5 w-2.5 text-fg-3/60 hover:text-fg-2 transition-colors cursor-help" />
        </Tooltip>
      </div>
      <div className={cn('data-mono text-lg font-bold mt-0.5', valueClass ?? 'text-fg-1')}>{value}</div>
    </div>
  )
}

function BaseCard({
  ts,
  onRun,
  running,
}: {
  ts: TestSetListItem
  onRun: (id: string) => void
  running: boolean
}) {
  const diag = diagnoseBase({
    runsCount: ts.runsCount,
    precision: ts.lastRunPrecision,
    coverage: ts.lastRunCoverage,
  })
  return (
    <Card className="group relative transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md">
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <h3 className="font-display font-bold text-[15px] leading-snug text-fg-1 truncate" title={ts.name}>
                <Link
                  to={`/benchmarks/${ts.id}`}
                  className="after:absolute after:inset-0 group-hover:text-brand-700 transition-colors"
                >
                  {formatBaseName(ts.name)}
                </Link>
              </h3>
              <Badge tone={diag.tone}>{diag.label}</Badge>
              {ts.lastRunStatus === 'RUNNING' && <Badge tone="warning">Rodando</Badge>}
              {ts.lastRunStatus === 'FAILED' && <Badge tone="danger">Falhou</Badge>}
            </div>
            <div className="flex items-center gap-2 text-xs text-fg-3 flex-wrap">
              <span>
                <span className="data-mono text-fg-2">{ts.totalRows}</span> linhas
              </span>
              <span className="text-border-strong">·</span>
              <span>
                <span className="data-mono text-fg-2">{ts.classLabels.length}</span> classes
              </span>
              <span className="text-border-strong">·</span>
              <span>
                <span className="data-mono text-fg-2">{ts.runsCount}</span> runs
              </span>
              {ts.lastRunAt && (
                <>
                  <span className="text-border-strong">·</span>
                  <span>última {new Date(ts.lastRunAt).toLocaleDateString('pt-BR')}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-5 shrink-0">
            <Metric
              label="Precisão"
              value={pct(ts.lastRunPrecision)}
              valueClass={precisionColor(ts.lastRunPrecision)}
              tip="Percentual de classificações corretas entre os matches que as regras fizeram."
            />
            <Metric
              label="Cobertura"
              value={pct(ts.lastRunCoverage)}
              tip="Percentual da base que recebeu alguma classificação das regras (o resto vai pro LLM)."
            />
          </div>
        </div>

        {/* ações secundárias reveladas no hover */}
        <div className="grid grid-rows-[0fr] transition-all duration-200 ease-out group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]">
          <div className="overflow-hidden">
            <div className="relative z-10 flex items-center gap-2 pt-3 mt-3 border-t border-border-subtle">
              <Link
                to={`/benchmarks/${ts.id}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 transition-colors"
              >
                Abrir resultados
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={() => onRun(ts.id)}
                disabled={running || ts.lastRunStatus === 'RUNNING'}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-fg-2 hover:bg-bg-subtle transition-colors disabled:opacity-50"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                {ts.runsCount === 0 ? 'Rodar com regras atuais' : 'Rodar novamente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export function TestSetsPage() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: testSetsQueryKey.list(),
    queryFn: testSetsApi.list,
  })

  const runMut = useMutation({
    mutationFn: (id: string) => testSetsApi.run(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: testSetsQueryKey.list() }),
  })

  // resumo executivo
  const bases = data ?? []
  const total = bases.length
  const criticas = bases.filter((ts) =>
    CRITICAL_LEVELS.includes(
      diagnoseBase({ runsCount: ts.runsCount, precision: ts.lastRunPrecision, coverage: ts.lastRunCoverage }).level,
    ),
  ).length
  const bestPrecision = bases.reduce<number | null>((best, ts) => {
    if (ts.lastRunPrecision == null) return best
    return best == null || ts.lastRunPrecision > best ? ts.lastRunPrecision : best
  }, null)
  const lastExecution = bases.reduce<Date | null>((latest, ts) => {
    if (!ts.lastRunAt) return latest
    const d = new Date(ts.lastRunAt)
    return latest == null || d > latest ? d : latest
  }, null)

  return (
    <div className="flex flex-col gap-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <span className="data-mono text-2xs uppercase tracking-wider text-accent-600 mb-2 inline-block">
            Studio · Assertividade
          </span>
          <h1 className="font-display text-2xl font-bold text-fg-1">Bases de teste</h1>
          <p className="text-sm text-fg-3 mt-1">
            Importe planilhas com publicações já classificadas pra medir a precisão das regras.
          </p>
        </div>
        <Button onClick={() => setDrawerOpen(true)}>
          <FileUp className="h-4 w-4" />
          Importar planilha
        </Button>
      </header>

      {!isLoading && total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat icon={BarChart3} label="Bases" value={String(total)} />
          <MiniStat
            icon={ShieldAlert}
            label="Críticas"
            value={String(criticas)}
            tone={criticas > 0 ? 'danger' : 'neutral'}
          />
          <MiniStat
            icon={Target}
            label="Melhor precisão"
            value={pct(bestPrecision)}
            tone={bestPrecision != null && bestPrecision >= 0.8 ? 'success' : 'neutral'}
          />
          <MiniStat
            icon={Clock}
            label="Última execução"
            value={lastExecution ? lastExecution.toLocaleDateString('pt-BR') : '—'}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-fg-3">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : bases.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="Nenhuma base ainda"
          description="Importe uma planilha (xlsx ou csv) com ID_EXTERNO, PROCESSO, TEXTO e CLASSE."
          action={
            <Button onClick={() => setDrawerOpen(true)}>
              <FileUp className="h-4 w-4" />
              Importar primeira base
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          {bases.map((ts) => (
            <BaseCard
              key={ts.id}
              ts={ts}
              onRun={(id) => runMut.mutate(id)}
              running={runMut.isPending && runMut.variables === ts.id}
            />
          ))}
        </div>
      )}

      <UploadTestSetDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  )
}
